import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import NavBar from './components/NavBar';
import Footer from './components/Footer';
import FacebookPixel from './components/FacebookPixel';
import SeoManager from './components/SeoManager';
import IdleMount from './components/IdleMount';
import Home from './pages/Home';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { WishlistProvider } from './context/WishlistContext';

const Categories = lazy(() => import('./pages/Categories'));
const ProductList = lazy(() => import('./pages/ProductList'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const UserOrderDetail = lazy(() => import('./pages/UserOrderDetail'));
const PaymentFailed = lazy(() => import('./pages/PaymentFailed'));
const Page = lazy(() => import('./pages/Page'));
const FloatingContact = lazy(() => import('./components/FloatingContact'));
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./admin/AdminDashboard'));
const AdminCategories = lazy(() => import('./admin/AdminCategories'));
const AdminCategoryCreate = lazy(() => import('./admin/AdminCategoryCreate'));
const AdminProducts = lazy(() => import('./admin/AdminProducts'));
const AdminSections = lazy(() => import('./admin/AdminSections'));
const AdminSectionEdit = lazy(() => import('./admin/AdminSectionEdit'));
const AdminProductEdit = lazy(() => import('./admin/AdminProductEdit'));
const AdminOrders = lazy(() => import('./admin/AdminOrders'));
const AdminCustomers = lazy(() => import('./admin/AdminCustomers'));
const AdminPages = lazy(() => import('./admin/AdminPages'));
const AdminSliders = lazy(() => import('./admin/AdminSliders'));
const AdminSliderEdit = lazy(() => import('./admin/AdminSliderEdit'));
const AdminSettings = lazy(() => import('./admin/AdminSettings'));
const AdminPayments = lazy(() => import('./admin/AdminPayments'));
const AdminCouriers = lazy(() => import('./admin/AdminCouriers'));
const AdminOrderDetail = lazy(() => import('./admin/AdminOrderDetail'));
const AdminCustomerDetail = lazy(() => import('./admin/AdminCustomerDetail'));
const AdminProductVariations = lazy(() => import('./admin/AdminProductVariations'));
const AdminPageEdit = lazy(() => import('./admin/AdminPageEdit'));
const AdminCoupons = lazy(() => import('./admin/AdminCoupons'));
const AdminReviews = lazy(() => import('./admin/AdminReviews'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <WishlistProvider>
       <CartProvider>
        <SettingsProvider>
          <BrowserRouter>
            <ScrollToTop />
            <SeoManager />
            <FacebookPixel />
            <NavBar />
            <main id="main-content">
              <Suspense fallback={<div className="container py-5" aria-live="polite">Loading…</div>}>
              <Routes>
              <Route path="/admin/*" element={<AdminLayout />}>
                <Route path="" element={<div className="container-fluid mt-4"><AdminDashboard /></div>} />
                <Route path="categories" element={<div className="container-fluid mt-4"><AdminCategories /></div>} />
                <Route path="categories/create" element={<div className="container-fluid mt-4"><AdminCategoryCreate /></div>} />
                <Route path="categories/edit/:id" element={<div className="container-fluid mt-4"><AdminCategoryCreate /></div>} />
                <Route path="products" element={<div className="container-fluid mt-4"><AdminProducts /></div>} />
                <Route path="products/create" element={<div className="container-fluid mt-4"><AdminProductEdit /></div>} />
                <Route path="products/edit/:id" element={<div className="container-fluid mt-4"><AdminProductEdit /></div>} />
                <Route path="products/:productId/variations" element={<div className="container-fluid mt-4"><AdminProductVariations /></div>} />
                <Route path="orders" element={<div className="container-fluid mt-4"><AdminOrders /></div>} />
                <Route path="coupons" element={<div className="container-fluid mt-4"><AdminCoupons /></div>} />
                <Route path="orders/:id" element={<div className="container-fluid mt-4"><AdminOrderDetail /></div>} />
                <Route path="customers" element={<div className="container-fluid mt-4"><AdminCustomers /></div>} />
                <Route path="customers/:id" element={<div className="container-fluid mt-4"><AdminCustomerDetail /></div>} />
                <Route path="sections" element={<div className="container-fluid mt-4"><AdminSections /></div>} />
                <Route path="sections/create" element={<div className="container-fluid mt-4"><AdminSectionEdit /></div>} />
                <Route path="sections/edit/:id" element={<div className="container-fluid mt-4"><AdminSectionEdit /></div>} />
                <Route path="pages" element={<div className="container-fluid mt-4"><AdminPages /></div>} />
                <Route path="pages/create" element={<div className="container-fluid mt-4"><AdminPageEdit /></div>} />
                <Route path="pages/edit/:id" element={<div className="container-fluid mt-4"><AdminPageEdit /></div>} />
                <Route path="sliders" element={<div className="container-fluid mt-4"><AdminSliders /></div>} />
                <Route path="sliders/create" element={<div className="container-fluid mt-4"><AdminSliderEdit /></div>} />
                <Route path="sliders/edit/:id" element={<div className="container-fluid mt-4"><AdminSliderEdit /></div>} />
                <Route path="settings" element={<div className="container-fluid mt-4"><AdminSettings /></div>} />
                <Route path="payments" element={<div className="container-fluid mt-4"><AdminPayments /></div>} />
                <Route path="couriers" element={<div className="container-fluid mt-4"><AdminCouriers /></div>} />
                <Route path="reviews" element={<div className="container-fluid mt-4"><AdminReviews /></div>} />
              </Route>

              <Route path="/" element={<div className=""><Home /></div>} />
              <Route path="/categories" element={<div className="container mt-4"><Categories /></div>} />
              <Route path="/products" element={<div className="container mt-4"><ProductList /></div>} />
              <Route path="/products/:slug" element={<div className="container mt-4"><ProductDetail /></div>} />
              <Route path="/cart" element={<div className="container mt-4"><Cart /></div>} />
              <Route path="/wishlist" element={<div className="container mt-4"><Wishlist /></div>} />
              <Route path="/checkout" element={<div className="container mt-4"><Checkout /></div>} />
              <Route path="/payment-failed" element={<PaymentFailed />} />

              <Route path="/page/:slug" element={<div className="container mt-4"><Page /></div>} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/profile" element={<div className="container mt-4"><UserProfile /></div>} />
              <Route path="/profile/orders/:id" element={<UserOrderDetail />} />
              <Route path="/order/:id" element={<UserOrderDetail />} />
              </Routes>
              </Suspense>
            </main>
            <IdleMount><Suspense fallback={null}><FloatingContact /></Suspense></IdleMount>
            <Footer />
          </BrowserRouter>
        </SettingsProvider>
       </CartProvider>
      </WishlistProvider>
    </AuthProvider>
  );
}
